import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

import { SignalStore } from './signal-store';
import { ApiService } from './api.service';
import { Tour } from './tour.service';
import { Message } from './messages.service';
import { Activity } from './activity.service';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  title: string;
  officePhone: string;
  userName: string;
  email: string;
  organizationId: string;
  organizationName: string;
  organizationType: string;
  institutionIid: number;
  twoFactorEnabled: boolean;
  twoFactorType: string;
  phoneNumber: string;
  emailConfirmed: boolean;
  claims: string[];
  unreadNews: number;
  unreadMessages: number;
  tours?: Tour[];
  message?: Message;
  activity: Activity[];
  products: UserProduct[];
}

export interface UserProduct {
  id: string;
  name: string;
  clientCode: string;
  tierClientCode: string;
  hasDashboard: boolean;
  dashboardComponent: string;
}

export interface Token {
  exp: number;
  iss: string;
  aud: string;
  uid: string;
  name: string;
  userName: string;
  isAdmin: boolean;
  isAuthenticated: boolean;
  claims: string[];
  status?: string;
  tfaType?: string;
  phone?: string;
  impersonate?: string;
}

export interface TfaSetupInfo {
  account: string;
  manualEntryKey: string;
  qrCodeSetupImageUrl: string;
}

export interface EditProfile {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
}

export interface InvitedUser {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService extends SignalStore<User> {
  private apiService = inject(ApiService);
  private cookieService = inject(CookieService);
  private router = inject(Router);

  private _token = signal<Token | undefined>(undefined);
  private _redirectUrl?: string;

  readonly userTours = signal<Tour[] | undefined>(undefined);
  readonly userProducts = computed(() => {
    if (!this.state().products || !this.state().claims) return [];
    return this.state().products.filter(p => this.state().claims.find(c => c == p.clientCode));
  });

  constructor() {
    super();
    const token = this.loadUserToken();
    const user = this.loadUser();
    if (token && user) {
      this._token.set(token);
      this.setState(user);
    }
  }

  // #region Authentication

  get isAuthenticated(): Signal<boolean> {
    return computed(() => (this._token()?.isAuthenticated && !this.isTokenExpired()) ?? false);
  }

  get isAdmin(): Signal<boolean> {
    return computed(() => this._token()?.isAdmin ?? false);
  }

  hasClaim(claim: string): boolean {
    return this._token()?.claims?.includes(claim) ?? false;
  }

  isImpersonating(): boolean {
    return (this._token()?.isAdmin && this._token()?.impersonate != undefined) ?? false;
  }

  get redirectUrl(): string | undefined {
    return this._redirectUrl;
  }

  set redirectUrl(url: string) {
    this._redirectUrl = url;
  }

  login(username: string, password: string): Observable<boolean> {
    return this.apiService.post<User>('api/auth/login', { username, password })
      .pipe(map((user) => {
        this.updateAndStoreUser(user);
        this.setTours(user.tours);
        this.handleLogin();
        return true;
      }));
  }

  handleLogin() {
    const token = this.loadUserToken();
    if (token) {
      this._token.set(token);
    }
    if (!this.isAuthenticated()) {
      switch (this._token()?.status) {
        case 'password':
          this.router.navigate(['/account/password']);
          return;
        case 'tfa-setup':
          this.router.navigate(['/account/tfasetup']);
          return;
        case 'tfa-verify':
          this.router.navigate(['/account/tfaverify']);
          return;
      }
    }
    if (this.isAdmin()) {
      window.location.replace('/Admin');
      return;
    }
    else {
      const redirectUrl = sessionStorage.getItem('redirectUrl');

      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        this.router.navigateByUrl(redirectUrl);
      } else if (this.redirectUrl) {
        const url = this.redirectUrl;
        this._redirectUrl = undefined;
        this.router.navigateByUrl(url);
      } else {
        this.router.navigate(['/dashboard']);
      }
    }
  }

  logout(): void {
    if (this.isAdmin()) {
      window.location.replace(`/Admin/Organization/Detail/${this.select('organizationId')()}`);
    }
    else {
      this.clearUser();
      this.clearUserToken();

      // partner

      this.router.navigate(['/']);
    }
  }

  setInstitution(iid: number): void {
    const user = this.state();
    user.institutionIid = iid;
    sessionStorage.setItem('user', JSON.stringify(user));
  }

  setTours(tours: Tour[] | undefined): void {
    const user = this.state();
    user.tours = tours;
    sessionStorage.setItem('user', JSON.stringify(user));
    this.userTours.set(tours);
  }

  private isTokenExpired(): boolean {
    const date = new Date(0);
    date.setUTCSeconds(this._token()?.exp ?? 0);
    return !(date.valueOf() > new Date().valueOf());
  }

  private loadUserToken(): Token | undefined {
    if (this.cookieService.check('token.body')) {
      const cookie = this.cookieService.get('token.body');
      return JSON.parse(cookie != undefined ? window.atob(cookie.split('.')[1]) : '{}') as Token;
    }
    else {
      return undefined;
    }
  }

  private clearUserToken(): void {
    this.cookieService.delete('token.body');
    this._token.set({} as Token);
  }

  private loadUser(): User | undefined {
    const json = sessionStorage.getItem('user');
    if (json) {
      const user = JSON.parse(json) as User;
      this.userTours.set(user.tours);
      return user;
    }
    else {
      return undefined;
    }
  }

  private updateAndStoreUser(user: User): void {
    this.setState(user);
    sessionStorage.setItem('user', JSON.stringify(this.state()));
  }

  private clearUser(): void {
    this.userTours.set(undefined);
    this.state.set({} as User);
    sessionStorage.removeItem('user');
  }

  // #endregion

  // #region Profile

  validateEmail(id: string, token: string, password: string): void {
    this.apiService.post<User>('api/account/validate', { id: id, token: token, password: password })
      .subscribe(user => {
        this.updateAndStoreUser(user);
        this.handleLogin();
      });
  }

  editProfile(model: EditProfile): Observable<boolean> {
    return this.apiService.put<User>('api/user/profile', model)
      .pipe(map((user) => {
        this.updateAndStoreUser(user);
        return true;
      }));
  }

  public changePassword(password: string): Observable<void> {
    return this.apiService.post<void>('api/auth/password', { password: password, force: false });
  }

  forceChangePassword(password: string): Observable<User> {
    return this.apiService.post<User>('api/auth/password', { password: password, force: true });
  }

  resetPasswordRequest(email: string): Observable<boolean> {
    return this.apiService.post<boolean>('api/auth/resetrequest', { email: email });
  }

  resetPassword(id: string, token: string, password: string): Observable<void> {
    return this.apiService.post<User>('api/auth/reset', { id: id, token: token, password: password })
      .pipe(map((user) => {
        this.updateAndStoreUser(user);
        this.handleLogin();
      }));
  }

  getInvitedUser(id: string): Observable<User> {
    return this.apiService.get<User>(`api/user/invite/${id}`)
  }

  updateInvitedUser(model: InvitedUser): Observable<User> {
    return this.apiService.put<User>('api/user/invite', model);
  }

  getUserName(): string {
    return this.state().userName;
  }

  refreshUserState(): Observable<void> {
    return this.apiService.get<User>('api/auth/refresh').pipe(
      map((user) => {
        this.updateAndStoreUser(user);

      })
    );
  }

  // #endregion

  // #region Two Factor Authentication

  public getTfaSetup(newKey = false): Observable<TfaSetupInfo> {
    return this.apiService.get<TfaSetupInfo>(`api/auth/tfasetup?newKey=${newKey}`);
  }

  public setTfaSetup(type: string, code: string, phoneNumber: string | null, login: boolean): Observable<boolean> {
    return this.apiService.post<User>('api/auth/tfasetup', { type, code, phoneNumber })
      .pipe(map((user) => {
        this.updateAndStoreUser(user);
        if (login) {
          this.handleLogin();
        }
        return true;
      }));
  }

  tfaSendCode(phoneNumber: string): Observable<void> {
    return this.apiService.post<void>('api/auth/tfasend', { phoneNumber });
  }

  tfaVerify(code: string): Observable<boolean> {
    return this.apiService.post<User>('api/auth/tfaverify', { code })
      .pipe(map(user => {
        this.updateAndStoreUser(user);
        this.handleLogin();
        return true;
      }));
  }

  // #endregion
}
